import React, { useState, useEffect } from 'react';
import { useFormik } from 'formik';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { useStateProvider } from "../manager/StateContext";
import { toast, ToastContainer } from 'react-toastify';
import {fallRiskRating, riskRatingOptions, homeCareServicesOptions, PressurSoreRisk, mobilityOptions,
  shiftPatternOptions, RecommendedWorkPattern} from "../services/data/heathWorkerData"
import 'react-toastify/dist/ReactToastify.css';

const AssessmentForm = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const [{ stage_token }] = useStateProvider();
  const { id: contractId } = useParams();

  // Initialize formik without validation
  const formik = useFormik({
    initialValues: {
      as_id: '',
      contID: contractId,
      client_name: '',
      gender: '',
      birth_date: '',
      marital_status: '',
      service_address: '',
      medical_history: '',
      medical_tools: '',
      allergies: '',
      date_plan: '',
      routine: '',
      risk: '',
      medication: '',
      mobility: '',
      rating: '',
      pressure: '',
      client_preference: '',
      frequency: '',
      pattern: '',
      assessor_comment: '',
      comment: '',
      services: {
        option1: null, option2: null, option3: null, option4: null,
        option5: null, option6: null, option7: null, option8: null,
        option9: null, option10: null, option11: null, option12: null,
        option13: null, option14: null, option15: null, option16: null
      }
    },
    onSubmit: async (values) => {
      try {
        await submitAssessment(values);
      } catch (error) {
        setError('Failed to submit assessment. Please try again.');
      }
    },
  });

  useEffect(() => {
    if (contractId) {
      const fetchAssessmentData = async () => {
        setIsLoading(true);
        try {
          const response = await axios.get(
            `${process.env.REACT_APP_API_LIVE_URL}/dashboard/homecare-appointment/assessment/${contractId}`,
            {
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${stage_token}`,
              },
            }
          );

          // Handle nested response structure from backend
          const assessmentData = response.data.data?.assessment || response.data;
          
          // Initialize services with all null values first
          const services = {
            option1: null, option2: null, option3: null, option4: null,
            option5: null, option6: null, option7: null, option8: null,
            option9: null, option10: null, option11: null, option12: null,
            option13: null, option14: null, option15: null, option16: null
          };
          
          // Process existing services if available
          if (assessmentData.services) {
            try {
              const existingServices = typeof assessmentData.services === 'string' 
                ? JSON.parse(assessmentData.services)
                : assessmentData.services;
                
              // Map the existing services to the option keys
              homeCareServicesOptions.forEach((service, index) => {
                const optionKey = `option${index + 1}`;
                
                // Check if this service exists in the DB data
                Object.entries(existingServices).forEach(([key, value]) => {
                  if (key === optionKey && value === service) {
                    services[optionKey] = service;
                  }
                });
                
                // Also check if the value matches the service name directly
                Object.values(existingServices).forEach(value => {
                  if (value === service) {
                    services[optionKey] = service;
                  }
                });
              });
            } catch (e) {
              console.error('Error parsing services:', e);
            }
          }
            
          formik.setValues({
            as_id: assessmentData._id || '',
            contID: contractId, 
            client_name: assessmentData.client_name || '',
            gender: assessmentData.gender || '',
            birth_date: assessmentData.birth_date || '',
            marital_status: assessmentData.marital_status || '',
            service_address: assessmentData.service_address || '',
            medical_history: assessmentData.medical_history || '',
            medical_tools: assessmentData.medical_tools || '',
            allergies: assessmentData.allergies || '',
            date_plan: assessmentData.date_plan || '',
            routine: assessmentData.routine || '',
            risk: assessmentData.risk || '',
            medication: assessmentData.medication || '',
            mobility: assessmentData.mobility || '',
            rating: assessmentData.rating || '',
            pressure: assessmentData.pressure || '',
            client_preference: assessmentData.client_preference || '',
            frequency: assessmentData.frequency || '',
            pattern: assessmentData.pattern || '',
            assessor_comment: assessmentData.assessor_comment || '',
            comment: assessmentData.patient_comment || '',
            services,
          });
          
        
          
        } catch (error) {
          console.error('Error fetching assessment data:', error);
          if (error) {
            toast.error(error.response?.data?.message || 'Failed to load assessment data');
          } 
          setError('Failed to load assessment data');
        } finally {
          setIsLoading(false);
        }
      };

      fetchAssessmentData();
    }
  }, [contractId, stage_token]);

  const submitAssessment = async (values) => {
    setIsSubmitting(true);
    try {
      const { services, ...restValues } = values;

      // Create a formData object with all the basic fields
      const formData = {
        ...restValues,
        contID: contractId,
      };

     
      Object.entries(services).forEach(([key, value]) => {
        formData[key] = value; // This will add option1, option2, etc. directly to formData
      });

      console.log('Final submission data:', formData);

      const response = await axios.post(
        `${process.env.REACT_APP_API_LIVE_URL}/api/create-assessment`,
        formData,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${stage_token}`,
          },
        }
      );
      
      toast.success(response.data.message || 'Assessment submitted successfully!', {
        autoClose: 6000,
        onClose: () => {
          // Reload the page after toast closes
          window.location.reload();
        }
      });

      return response.data;
    } catch (error) {
      console.error('Submission error:', error);
      toast.error(error.response?.data?.message || 'Submission failed');
      throw error;
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleServiceChange = (service, index) => {
    const optionKey = `option${index + 1}`;
    // Toggle between the service value and null
    formik.setFieldValue(`services.${optionKey}`, 
      formik.values.services[optionKey] === service ? null : service
    );
  };

  if (isLoading) {
    return (
      <div className="max-w-4xl mx-auto p-6 bg-white rounded-lg shadow-md flex justify-center items-center h-64">
        <div className="flex flex-col items-center">
          <svg className="animate-spin h-12 w-12 text-blue-600 mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <span className="text-lg font-medium text-gray-700">Loading assessment data...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto p-6 bg-white rounded-lg shadow-md">
        <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        </div>
        <button
          onClick={() => window.location.reload()}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors duration-200"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6 bg-white rounded-lg shadow-md">
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-gray-800 mb-2">
          {'Create Assessment'}
        </h2>
        <p className="text-gray-600">
          {'Fill out the patient assessment details below'}
        </p>
      </div>
      
      <form onSubmit={formik.handleSubmit} className="space-y-8">
        {/* Personal Information Section */}
        <div className="bg-gray-50 p-6 rounded-lg">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Personal Information</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
              <input
                type="text"
                name="client_name"
                value={formik.values.client_name}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 transition-colors"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Gender</label>
              <div className="flex space-x-4">
                <label className="inline-flex items-center">
                  <input
                    type="radio"
                    name="gender"
                    value="Male"
                    checked={formik.values.gender === 'Male'}
                    onChange={formik.handleChange}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                  />
                  <span className="ml-2">Male</span>
                </label>
                <label className="inline-flex items-center">
                  <input
                    type="radio"
                    name="gender"
                    value="Female"
                    checked={formik.values.gender === 'Female'}
                    onChange={formik.handleChange}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                  />
                  <span className="ml-2">Female</span>
                </label>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Date of Birth</label>
              <input
                type="date"
                name="birth_date"
                value={formik.values.birth_date}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 transition-colors"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Marital Status</label>
              <select
                name="marital_status"
                value={formik.values.marital_status}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 transition-colors"
              >
                <option value="">Select Status</option>
                <option value="Single">Single</option>
                <option value="Married">Married</option>
                <option value="Divorced">Divorced</option>
                <option value="Widow/Widower">Widow/Widower</option>
              </select>
            </div>
          </div>
        </div>

        {/* Address Section */}
        <div className="bg-gray-50 p-6 rounded-lg">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Service Address</h3>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Service Address</label>
            <textarea
              name="service_address"
              rows={3}
              value={formik.values.service_address}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 transition-colors"
            />
          </div>
        </div>

        {/* Medical Information Section */}
        <div className="bg-gray-50 p-6 rounded-lg">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Medical Information</h3>
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Brief Medical History</label>
              <textarea
                name="medical_history"
                rows={4}
                value={formik.values.medical_history}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 transition-colors"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Any medical aids or tools for vital obs currently in use?
              </label>
              <input
                type="text"
                name="medical_tools"
                value={formik.values.medical_tools}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 transition-colors"
                placeholder="e.g., Oxygen tank, blood pressure monitor"
              />
            </div>
          </div>
        </div>

        {/* Home Care Services Section */}
        <div className="bg-gray-50 p-6 rounded-lg">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Home Care Services</h3>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">HomeCare Services</label>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {homeCareServicesOptions.map((service, index) => {
                const optionKey = `option${index + 1}`;
                const isSelected = formik.values.services[optionKey] === service;
                
                return (
                  <div key={service} className="flex items-start">
                    <div className="flex items-center h-5">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => handleServiceChange(service, index)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                    </div>
                    <div className="ml-3 text-sm">
                      <label className="font-medium text-gray-700">{service}</label>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Additional Medical Information */}
        <div className="bg-gray-50 p-6 rounded-lg">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Additional Medical Information</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Any ongoing routine hospital visits or medical home care?
              </label>
              <div className="flex space-x-4">
                <label className="inline-flex items-center">
                  <input
                    type="radio"
                    name="routine"
                    value="yes"
                    checked={formik.values.routine === 'yes'}
                    onChange={formik.handleChange}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                  />
                  <span className="ml-2">Yes</span>
                </label>
                <label className="inline-flex items-center">
                  <input
                    type="radio"
                    name="routine"
                    value="no"
                    checked={formik.values.routine === 'no'}
                    onChange={formik.handleChange}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                  />
                  <span className="ml-2">No</span>
                </label>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Continence Risk Rating</label>
              <select
                name="risk"
                value={formik.values.risk}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 transition-colors"
              >
                <option value="">Select Rating</option>
                {riskRatingOptions.map(option => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Prescribed Medication Regime</label>
              <select
                name="medication"
                value={formik.values.medication}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 transition-colors"
              >
                <option value="">Select Regime</option>
                <option value="Not On Any Prescribed Medication">Not On Any Prescribed Medication</option>
                <option value="Able to Take Prescribed Drugs Independetly ">Able to Take Prescribed Drugs Independetly</option>
                <option value="Require Limited Assistance ">Require Limited Assistance </option>
                <option value="Require Full Assistance of a Licenced Health Professional">Require Full Assistance of a Licenced Health Professional</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Any known allergies?</label>
              <input
                type="text"
                name="allergies"
                value={formik.values.allergies}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 transition-colors"
                placeholder="List any known allergies"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Fall Risk Rating</label>
              <select
                name="rating"
                value={formik.values.rating}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 transition-colors"
              >
                <option value="">Select Rating</option>
                {fallRiskRating.map(option => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Commencement date of Care</label>
              <input
                type="date"
                name="date_plan"
                value={formik.values.date_plan}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 transition-colors"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Pressure Sore Risk Rating</label>
              <select
                name="pressure"
                value={formik.values.pressure}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 transition-colors"
              >
                <option value="">Select Rating</option>
                {PressurSoreRisk.map(option => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Mobility Status</label>
              <select
                name="mobility"
                value={formik.values.mobility}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 transition-colors"
              >
                <option value="">Select Status</option>
                {mobilityOptions.map(option => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Caregiver Preferences */}
        <div className="bg-gray-50 p-6 rounded-lg">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Caregiver Preferences</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Specific preferences (e.g. for Caregiver's tribe or gender)
              </label>
              <input
                type="text"
                name="client_preference"
                value={formik.values.client_preference}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 transition-colors"
                placeholder="Any specific caregiver preferences"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Duration of Shift Pattern for Caregiver</label>
              <select
                name="frequency"
                value={formik.values.frequency}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 transition-colors"
              >
                <option value="">Select Shift Pattern</option>
                {shiftPatternOptions.map(option => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Recommended Work Pattern For Caregiver</label>
              <select
                name="pattern"
                value={formik.values.pattern}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 transition-colors"
              >
                <option value="">Select Work Pattern</option>
                {RecommendedWorkPattern.map(option => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Comments Section */}
        <div className="bg-gray-50 p-6 rounded-lg">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Comments</h3>
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Assessor's Comments</label>
              <textarea
                name="assessor_comment"
                rows={4}
                value={formik.values.assessor_comment}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 transition-colors"
                placeholder="Enter your assessment comments here"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Any comments from patient?</label>
              <textarea
                name="comment"
                rows={4}
                value={formik.values.comment}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 transition-colors"
                placeholder="Enter patient comments here (if any)"
              />
            </div>
          </div>
        </div>

        {/* Submit Button */}
        <div className="flex justify-end pt-4">
          <button
            type="submit"
            disabled={isSubmitting}
            className="px-6 py-3 bg-blue-600 text-white font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 transition-colors duration-200"
          >
            {isSubmitting ? (
              <span className="flex items-center">
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                {'Submitting...'}
              </span>
            ) : (
              'Submit Assessment'
            )}
          </button>
        </div>
      </form>
      <ToastContainer position="bottom-right" autoClose={5000} />
    </div>
  );
};

export default AssessmentForm;